import * as Sentry from '@sentry/react'
import { theme as styledTheme, Loader } from '@gnosis.pm/safe-react-components'
import { useEffect, useState } from 'react'
import { setBaseUrl } from '@gnosis.pm/safe-react-gateway-sdk'

import { LoadingContainer } from 'src/components/LoaderContainer'
import App from 'src/components/App'
import GlobalErrorBoundary from 'src/components/GlobalErrorBoundary'
import AppRoutes from 'src/routes'
import { store } from 'src/store'
import { history, STARTER_ROUTE } from 'src/routes/routes'
import theme from 'src/theme/mui'
import { wrapInSuspense } from 'src/utils/wrapInSuspense'
import Providers from '../Providers'
// import './index.module.scss'
import './OnboardCustom.module.scss'
import './KeystoneCustom.module.scss'
import 'src/walliro/assets/css/bootstrap.min.css'
import 'src/walliro/assets/sass/globals.scss'
import LegacyRouteRedirection from './LegacyRouteRedirection'
import { logError, Errors, CodedException } from 'src/logic/exceptions/CodedException'
import { loadChains } from 'src/config/cache/chains'
import { setChainId } from 'src/logic/config/utils'
import { _getChainId } from 'src/config'
import { GATEWAY_URL } from 'src/utils/constants'
import { Provider as ProviderWalliro } from 'src/walliro/hook'
import Starter from 'src/routes/WalliroStarter'
import { Route, Switch } from 'react-router-dom'
import { background } from 'src/theme/variables'

// Preloader is rendered outside of '#root' and acts as a loading spinner
// for the app and then chains loading
const removePreloader = () => {
  document.getElementById('safe-preloader-animation')?.remove()
}

const RootConsumer = (): React.ReactElement | null => {
  const [hasChains, setHasChains] = useState<boolean>(false)
  const [isError, setIsError] = useState<boolean>(false)

  useEffect(() => {
    // Initialize the SDK
    setBaseUrl(GATEWAY_URL)

    const initChains = async () => {
      try {
        await loadChains()
        setHasChains(true)
      } catch (err) {
        logError(Errors._904, err.message)
        setIsError(true)
      }
    }
    initChains()

    // Set store chainId and init contracts/session

    setChainId(_getChainId())
  }, [])

  // Chains failed to load
  if (isError) {
    removePreloader()
    throw new CodedException(Errors._904)
  }

  if (!hasChains) {
    return null
  }

  removePreloader()

  return (
    <Switch>
      <Route component={Starter} path={STARTER_ROUTE} />
      <Route
        exact
        component={() => (
          <App>
            {wrapInSuspense(
              <AppRoutes />,
              <LoadingContainer>
                <Loader size="md" />
              </LoadingContainer>,
            )}
          </App>
        )}
        path={'/*'}
      />
    </Switch>
  )
}

// Chains loader requires error boundary, which requires Providers
// and Legacy redirection should be outside of Providers

const styledThemeWalliro = {
  ...styledTheme,
  colors: {
    ...styledTheme.colors,
    primary: 'rgb(var(--Mprimary))',
    primaryHover: 'rgb(var(--Mprimary-darker))',
    primaryLight: 'rgb(var(--Mprimary-lighter))',
    white: 'rgb(var(--Mwhite))',
    text: 'rgb(var(--Mtext))',
    secondary: 'rgb(var(--Mtext))',
    background: background,
    icon: 'rgb(var(--Mtext))',
    secondaryLight: 'rgb(var(--Mgray-darker))',
    inputField: 'rgba(var(--Mgray-darkest),1)',
    placeHolder: 'rgb(var(--Mtxt))',
  },
  fonts: {
    ...styledTheme.fonts,
    fontFamily: "'Montserrat', sans-serif",
  },
}

const Root = (): React.ReactElement => (
  <>
    <LegacyRouteRedirection history={history} />
    <ProviderWalliro>
      <Providers store={store} history={history} styledTheme={styledThemeWalliro} muiTheme={theme}>
        <Sentry.ErrorBoundary fallback={GlobalErrorBoundary}>
          <RootConsumer />
        </Sentry.ErrorBoundary>
      </Providers>
    </ProviderWalliro>
  </>
)

export default Root
